/**
 * Export Utilities - Phase 4
 *
 * 数据导出工具函数
 * 支持 CSV 和 Excel 格式导出
 */

import { ContainerTask } from '../types/container';
import { lfdDateOnly } from './lfdDate';
import { toast } from 'sonner';

// ==================== CSV Export ====================

/**
 * 将数据导出为 CSV 格式
 * @param tasks - 任务数据数组
 * @param filename - 导出文件名 (不含扩展名)
 */
export const exportToCSV = (tasks: ContainerTask[], filename: string = 'dispatch_tasks'): void => {
  if (!tasks || tasks.length === 0) {
    console.warn('No data to export');
    return;
  }

  // 定义导出列
  const columns = [
    { key: 'taskId', header: 'Task ID' },
    { key: 'containerNumber', header: 'Container Number' },
    { key: 'status', header: 'Status' },
    { key: 'taskType', header: 'Task Type' },
    { key: 'driver', header: 'Driver' },
    { key: 'shipper', header: 'Shipper' },
    { key: 'warehouse', header: 'Warehouse' },
    { key: 'warehouseAddress', header: 'Warehouse Address' },
    { key: 'portAppointment', header: 'Port Appointment' },
    { key: 'warehouseAppointment', header: 'Warehouse Appointment' },
    { key: 'lfd', header: 'LFD' },
    { key: 'containerSize', header: 'Container Size' },
    { key: 'containerType', header: 'Container Type' },
    { key: 'shippingLine', header: 'Shipping Line' },
    { key: 'currentLocation', header: 'Current Location' },
    { key: 'internalNote', header: 'Internal Note' },
    { key: 'lastUpdate', header: 'Last Update' }
  ];

  // 创建 CSV 内容
  const header = columns.map(col => `"${col.header}"`).join(',');

  const rows = tasks.map(task => {
    return columns.map(col => {
      // LFD 是日历日，不是时刻 —— 导出 YYYY-MM-DD，别把整条 ISO 时间戳倒进表格
      const value = col.key === 'lfd'
        ? lfdDateOnly(task.lfd)
        : task[col.key as keyof ContainerTask];
      // 处理特殊字符和换行
      if (value === null || value === undefined) {
        return '""';
      }
      const stringValue = String(value).replace(/"/g, '""').replace(/\n/g, ' ');
      return `"${stringValue}"`;
    }).join(',');
  });

  const csvContent = [header, ...rows].join('\n');

  // 添加 BOM 以支持中文在 Excel 中正确显示
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

  // 下载文件
  downloadBlob(blob, `${filename}_${formatDateForFilename()}.csv`);
};

// ==================== Excel Export ====================

/**
 * 将数据导出为 Excel 格式 (使用 xlsx 风格的 XML)
 * 注意: 这是简化版本，不需要额外依赖
 * @param tasks - 任务数据数组
 * @param filename - 导出文件名 (不含扩展名)
 */
export const exportToExcel = (tasks: ContainerTask[], filename: string = 'dispatch_tasks'): void => {
  if (!tasks || tasks.length === 0) {
    console.warn('No data to export');
    return;
  }

  // 定义导出列
  const columns = [
    { key: 'taskId', header: 'Task ID', width: 15 },
    { key: 'containerNumber', header: 'Container Number', width: 18 },
    { key: 'status', header: 'Status', width: 12 },
    { key: 'taskType', header: 'Task Type', width: 12 },
    { key: 'driver', header: 'Driver', width: 15 },
    { key: 'shipper', header: 'Shipper', width: 20 },
    { key: 'warehouse', header: 'Warehouse', width: 25 },
    { key: 'warehouseAddress', header: 'Warehouse Address', width: 35 },
    { key: 'portAppointment', header: 'Port Appointment', width: 18 },
    { key: 'warehouseAppointment', header: 'Warehouse Appt', width: 18 },
    { key: 'lfd', header: 'LFD', width: 12 },
    { key: 'containerSize', header: 'Size', width: 8 },
    { key: 'containerType', header: 'Type', width: 8 },
    { key: 'shippingLine', header: 'Shipping Line', width: 15 },
    { key: 'currentLocation', header: 'Location', width: 20 },
    { key: 'internalNote', header: 'Internal Note', width: 30 }
  ];

  // 创建 XML 表格内容
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<?mso-application progid="Excel.Sheet"?>\n';
  xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
  xml += '  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';

  // 样式定义
  xml += '  <Styles>\n';
  xml += '    <Style ss:ID="Header">\n';
  xml += '      <Font ss:Bold="1" ss:Color="#FFFFFF"/>\n';
  xml += '      <Interior ss:Color="#4472C4" ss:Pattern="Solid"/>\n';
  xml += '      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>\n';
  xml += '    </Style>\n';
  xml += '    <Style ss:ID="Data">\n';
  xml += '      <Alignment ss:Vertical="Center" ss:WrapText="1"/>\n';
  xml += '    </Style>\n';
  xml += '  </Styles>\n';

  // 工作表
  xml += '  <Worksheet ss:Name="Dispatch Tasks">\n';
  xml += '    <Table>\n';

  // 列宽
  columns.forEach(col => {
    xml += `      <Column ss:Width="${col.width * 7}"/>\n`;
  });

  // 表头行
  xml += '      <Row>\n';
  columns.forEach(col => {
    xml += `        <Cell ss:StyleID="Header"><Data ss:Type="String">${escapeXml(col.header)}</Data></Cell>\n`;
  });
  xml += '      </Row>\n';

  // 数据行
  tasks.forEach(task => {
    xml += '      <Row>\n';
    columns.forEach(col => {
      const value = col.key === 'lfd'
        ? lfdDateOnly(task.lfd)
        : task[col.key as keyof ContainerTask];
      const cellValue = value === null || value === undefined ? '' : String(value);
      xml += `        <Cell ss:StyleID="Data"><Data ss:Type="String">${escapeXml(cellValue)}</Data></Cell>\n`;
    });
    xml += '      </Row>\n';
  });

  xml += '    </Table>\n';
  xml += '  </Worksheet>\n';
  xml += '</Workbook>';

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
  downloadBlob(blob, `${filename}_${formatDateForFilename()}.xls`);
};

// ==================== Helper Functions ====================

/**
 * 下载 Blob 文件
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 格式化日期用于文件名
 */
const formatDateForFilename = (): string => {
  const now = new Date();
  return now.toISOString().slice(0, 10).replace(/-/g, '');
};

/**
 * 转义 XML 特殊字符
 */
const escapeXml = (str: string): string => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

// ==================== Export Dispatcher (Combined Utility) ====================

interface ExportOptions {
  format: 'csv' | 'excel';
  filename?: string;
  selectedOnly?: boolean;
}

/**
 * 导出任务数据
 * @param tasks - 所有任务
 * @param selectedIds - 选中的任务 ID (可选)
 * @param options - 导出选项
 */
export const exportTasks = (
  tasks: ContainerTask[],
  selectedIds: string[] = [],
  options: ExportOptions = { format: 'csv' }
): void => {
  const { format, filename = 'dispatch_tasks', selectedOnly = false } = options;

  // 如果指定只导出选中的，则过滤
  const dataToExport = selectedOnly && selectedIds.length > 0
    ? tasks.filter(task => selectedIds.includes(task.id))
    : tasks;

  if (dataToExport.length === 0) {
    toast.warning('Nothing to export.');
    return;
  }

  switch (format) {
    case 'csv':
      exportToCSV(dataToExport, filename);
      break;
    case 'excel':
      exportToExcel(dataToExport, filename);
      break;
    default:
      console.error('Unsupported export format:', format);
  }
};

